import React, { Suspense, lazy, useEffect, useState } from 'react';
import NotificationManager from './components/Notification/NotificationManager';
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { LoadScript } from '@react-google-maps/api';
import ErrorBoundary from './components/common/ErrorBoundary';
import PremiumFeatureModal from './components/common/PremiumFeatureModal';

// Eager load auth-related pages (critical for initial load)
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import VerificationCode from './pages/VerificationCode';
import SetNewPassword from './pages/SetNewPassword';
import ProtectedRoute from './auth/protectedRoutes';
import { AuthProvider } from './context/AuthContext';
import { GuidedTourProvider } from './context/GuidedTourContext';

// Lazy load protected routes (code splitting for better performance)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const ManageEmployee = lazy(() => import('./pages/ManageEmployee'));
const ManageProjects = lazy(() => import('./pages/ManageProjects'));
const ManageJob = lazy(() => import('./pages/ManageJob'));
const ManageShift = lazy(() => import('./pages/ManageShift'));
const LoginMonitor = lazy(() => import('./pages/LoginMonitor'));
const ManageLeave = lazy(() => import('./pages/ManageLeave'));
const LocationTracking = lazy(() => import('./pages/LocationTracking'));
const Reports = lazy(() => import('./pages/Reports'));
const Payroll = lazy(() => import('./pages/Payroll'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));

// Placeholder (optional)
const UserLogins = () => <div className="p-6 text-white">User Logins Page</div>;

const GOOGLE_MAPS_LIBRARIES = ['places'];

const GoogleMapsProvider = ({ children }) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!googleMapsApiKey) {
    return children;
  }

  return (
    <LoadScript googleMapsApiKey={googleMapsApiKey} libraries={GOOGLE_MAPS_LIBRARIES}>
      {children}
    </LoadScript>
  );
};

const App = () => {
    const [blockedFeature, setBlockedFeature] = useState(null);

    useEffect(() => {
      const handleForbiddenNotice = (event) => {
        const detail = event?.detail || {};
        setBlockedFeature({
          featureName: detail.featureName || 'This feature',
          endpoint: detail.endpoint || '',
        });
      };

      window.addEventListener('tenant-feature-forbidden', handleForbiddenNotice);
      return () => window.removeEventListener('tenant-feature-forbidden', handleForbiddenNotice);
    }, []);

    return (
      <ErrorBoundary>
        <AuthProvider>
          <GuidedTourProvider>
          {blockedFeature && (
            <PremiumFeatureModal
              isOpen={!!blockedFeature}
              featureName={blockedFeature.featureName}
              endpoint={blockedFeature.endpoint}
              onClose={() => setBlockedFeature(null)}
            />
          )}
          <NotificationManager />
          <Router>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="verify-code" element={<VerificationCode />} />
              <Route path="set-new-password" element={<SetNewPassword />} />

              {/* Super Admin routes */}
              <Route element={<ProtectedRoute allowedRoles={['SuperAdmin']} redirectPath="/" />}>
                <Route path="/super-admin/*" element={
                  <Suspense fallback={null}>
                    <SuperAdminPanel />
                  </Suspense>
                } />
              </Route>

              {/* Tenant Admin protected routes */}
              <Route element={<GoogleMapsProvider><ProtectedRoute allowedRoles={['Admin', 'Manager']} requireTenant={true} redirectPath="/" /></GoogleMapsProvider>}>
                <Route path="/dashboard" element={
                  <Suspense fallback={null}>
                    <Dashboard />
                  </Suspense>
                } />
                <Route path="/settings" element={
                  <Suspense fallback={null}>
                    <Settings />
                  </Suspense>
                } />
                <Route path="/manage-employees" element={
                  <Suspense fallback={null}>
                    <ManageEmployee />
                  </Suspense>
                } />
                <Route path="/user-logins" element={<UserLogins />} />
                <Route path="/manage-projects" element={
                  <Suspense fallback={null}>
                    <ManageProjects />
                  </Suspense>
                } />
                <Route path="/manage-job" element={
                  <Suspense fallback={null}>
                    <ManageJob />
                  </Suspense>
                } />
                <Route path="/manage-shifts" element={
                  <Suspense fallback={null}>
                    <ManageShift />
                  </Suspense>
                } />
                <Route path="/login-monitor" element={
                  <Suspense fallback={null}>
                    <LoginMonitor />
                  </Suspense>
                } />
                <Route path="/manage-leave" element={
                  <Suspense fallback={null}>
                    <ManageLeave />
                  </Suspense>
                } />
                <Route path="/reports" element={
                  <Suspense fallback={null}>
                    <Reports />
                  </Suspense>
                } />
                <Route path="/payroll-billing" element={
                  <Suspense fallback={null}>
                    <Payroll />
                  </Suspense>
                } />
                <Route path="/payroll-manager" element={<Navigate to="/payroll-billing" replace />} />
                <Route path="/location-tracking" element={
                  <Suspense fallback={null}>
                    <LocationTracking />
                  </Suspense>
                } />
              </Route>
            </Routes>
          </Router>
          </GuidedTourProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
};

export default App;
